/**
 * Strict structural diff between two R0002 Toast page structures.
 *
 * Used by compare mode to prove the Lambda reproduces the browser output.
 *
 * Two rules make this stricter than a naive deep-equal, and both exist because
 * of how mergePOSData2 consumes the result:
 *
 *   1. KEY SETS are compared, not just values. mergePOSData2 uses
 *      `rowData.cFOHHours !== undefined` as a "was this Toast-merged?" sentinel
 *      and reads the underscore-prefixed hour fields by name. A missing key is
 *      invisible to a value-only diff yet silently changes the merge.
 *
 *   2. TYPES are compared before values. rowData mixes formatted strings with
 *      raw numbers, so '0' vs 0 must register as a difference — it would survive
 *      == and often survive rendering, then break arithmetic downstream.
 *
 * @module clients/grm/views/R0002/ToastData/diffToastPages
 */

/**
 * Index a page's rows by store name.
 *
 * @param {Object} page - { pageId, data: [ { rowType, rowData } ] }
 * @returns {Map<string, Object>}
 */
function rowsByStore(page) {
  const map = new Map()
  const rows = (page && page.data) || []
  for (const row of rows) {
    const store = row && row.rowData && row.rowData.store
    if (store !== undefined) map.set(store, row)
  }
  return map
}

/**
 * Diff two page structures.
 *
 * @param {Array} oldPages - Legacy output.
 * @param {Array} newPages - Lambda output.
 * @returns {{pageIdMismatch: Array, storesOnlyInOld: Array, storesOnlyInNew: Array,
 *   keySetDiffs: Array, fieldDiffs: Array, rowTypeDiffs: Array}}
 */
export default function diffToastPages(oldPages, newPages) {
  const result = {
    pageIdMismatch: [],
    storesOnlyInOld: [],
    storesOnlyInNew: [],
    keySetDiffs: [],
    fieldDiffs: [],
    rowTypeDiffs: []
  }

  const oldArr = Array.isArray(oldPages) ? oldPages : []
  const newArr = Array.isArray(newPages) ? newPages : []

  if (oldArr.length !== newArr.length) {
    result.pageIdMismatch.push({
      reason: 'page count',
      oldVal: oldArr.length,
      newVal: newArr.length
    })
  }

  const pageCount = Math.max(oldArr.length, newArr.length)

  for (let i = 0; i < pageCount; i += 1) {
    const oldPage = oldArr[i]
    const newPage = newArr[i]
    const oldId = oldPage && oldPage.pageId
    const newId = newPage && newPage.pageId

    // Catches a missing or spurious '_empty' suffix, which downstream tests with
    // .includes('_empty') to decide whether a source contributed anything.
    if (oldId !== newId) {
      result.pageIdMismatch.push({ index: i, oldVal: oldId, newVal: newId })
    }

    const oldRows = rowsByStore(oldPage)
    const newRows = rowsByStore(newPage)

    for (const store of oldRows.keys()) {
      if (!newRows.has(store)) result.storesOnlyInOld.push({ pageId: oldId, store })
    }
    for (const store of newRows.keys()) {
      if (!oldRows.has(store)) result.storesOnlyInNew.push({ pageId: newId, store })
    }

    for (const [store, oldRow] of oldRows) {
      const newRow = newRows.get(store)
      if (!newRow) continue

      if (oldRow.rowType !== newRow.rowType) {
        result.rowTypeDiffs.push({
          pageId: oldId, store, oldVal: oldRow.rowType, newVal: newRow.rowType
        })
      }

      const oldData = oldRow.rowData || {}
      const newData = newRow.rowData || {}
      const oldKeys = Object.keys(oldData).sort()
      const newKeys = Object.keys(newData).sort()

      const missing = oldKeys.filter(k => !newKeys.includes(k))
      const extra = newKeys.filter(k => !oldKeys.includes(k))
      if (missing.length || extra.length) {
        result.keySetDiffs.push({ pageId: oldId, store, missing, extra })
      }

      for (const key of oldKeys) {
        if (!newKeys.includes(key)) continue
        const oldVal = oldData[key]
        const newVal = newData[key]
        // Type first: '0' and 0 are a real difference here.
        if (typeof oldVal !== typeof newVal || !Object.is(oldVal, newVal)) {
          result.fieldDiffs.push({
            pageId: oldId,
            store,
            key,
            oldVal,
            newVal,
            oldType: typeof oldVal,
            newType: typeof newVal
          })
        }
      }
    }
  }

  return result
}
